import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Input,
  Select,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  message,
  Popconfirm,
  Avatar,
} from 'antd';
import {
  SearchOutlined,
  StopOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  CrownOutlined,
  UserOutlined,
  ExperimentOutlined,
} from '@ant-design/icons';
import { getUserList, banUser, unbanUser, deleteUser, updateUserVIP, updateUserTester } from '../services/api';
import dayjs from 'dayjs';

const { Option } = Select;

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState({ keyword: '', status: undefined, is_vip: undefined });
  const [banModalVisible, setBanModalVisible] = useState(false);
  const [vipModalVisible, setVipModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [banForm] = Form.useForm();
  const [vipForm] = Form.useForm();

  useEffect(() => {
    loadUsers();
  }, [pagination.current, pagination.pageSize]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await getUserList({
        page: pagination.current,
        page_size: pagination.pageSize,
        ...filters,
      });
      setUsers(data.list || []);
      setPagination((prev) => ({ ...prev, total: data.total }));
    } catch (error) {
      console.error('加载用户失败', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    loadUsers();
  };

  const handleBan = (user) => {
    setSelectedUser(user);
    banForm.resetFields();
    setBanModalVisible(true);
  };

  const handleBanSubmit = async () => {
    try {
      const values = await banForm.validateFields();
      await banUser(selectedUser.id, values);
      message.success('用户已封禁');
      setBanModalVisible(false);
      loadUsers();
    } catch (error) {
      console.error('封禁失败', error);
    }
  };

  const handleUnban = async (id) => {
    try {
      await unbanUser(id);
      message.success('用户已解封');
      loadUsers();
    } catch (error) {
      console.error('解封失败', error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteUser(id);
      message.success('用户已删除');
      loadUsers();
    } catch (error) {
      console.error('删除失败', error);
    }
  };

  const handleVIP = (user) => {
    setSelectedUser(user);
    vipForm.setFieldsValue({
      vip_level: user.vip_level || 0,
      days: 30,
    });
    setVipModalVisible(true);
  };

  const handleVIPSubmit = async () => {
    try {
      const values = await vipForm.validateFields();
      await updateUserVIP(selectedUser.id, values);
      message.success('会员状态已更新');
      setVipModalVisible(false);
      loadUsers();
    } catch (error) {
      console.error('更新会员失败', error);
    }
  };

  const handleToggleTester = async (user) => {
    try {
      const newStatus = user.is_tester === 1 ? 0 : 1;
      await updateUserTester(user.id, { is_tester: newStatus });
      message.success(newStatus === 1 ? '已设置为测试用户' : '已取消测试权限');
      loadUsers();
    } catch (error) {
      console.error('更新测试权限失败', error);
    }
  };

  const getVIPLevelText = (level) => {
    const levels = { 0: '普通用户', 1: '月度会员', 2: '年度会员', 3: '永久会员' };
    return levels[level] || '普通用户';
  };

  const columns = [
    {
      title: '用户',
      key: 'user',
      width: 150,
      fixed: 'left',
      render: (_, record) => (
        <Space>
          <Avatar
            src={record.avatar}
            icon={<UserOutlined />}
            style={{ backgroundColor: '#667eea' }}
          />
          <div>
            <div style={{ fontWeight: 500 }}>{record.username}</div>
            <div style={{ fontSize: 12, color: '#999' }}>{record.nickname || '-'}</div>
          </div>
        </Space>
      ),
    },
    {
      title: '联系方式',
      key: 'contact',
      width: 200,
      render: (_, record) => (
        <div>
          <div>{record.email || '-'}</div>
          <div style={{ fontSize: 12, color: '#999' }}>{record.phone || '-'}</div>
        </div>
      ),
    },
    {
      title: '状态',
      key: 'status',
      width: 100,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Tag color={record.status === 1 ? 'green' : 'red'}>
            {record.status === 1 ? '正常' : '禁用'}
          </Tag>
          {record.is_tester === 1 && (
            <Tag color="purple">
              <ExperimentOutlined /> 测试用户
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: '会员',
      key: 'vip',
      width: 120,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Tag color={record.is_vip ? 'gold' : 'default'}>
            {record.is_vip ? <CrownOutlined /> : null} {getVIPLevelText(record.vip_level)}
          </Tag>
          {record.vip_expire_at && record.vip_level !== 3 && (
            <span style={{ fontSize: 12, color: '#999' }}>
              到期: {dayjs(record.vip_expire_at).format('YYYY-MM-DD')}
            </span>
          )}
        </Space>
      ),
    },
    {
      title: '登录信息',
      key: 'login',
      width: 150,
      render: (_, record) => (
        <div>
          <div>登录次数: {record.login_count}</div>
          <div style={{ fontSize: 12, color: '#999' }}>
            最后登录: {record.last_login_at ? dayjs(record.last_login_at).format('MM-DD HH:mm') : '-'}
          </div>
        </div>
      ),
    },
    {
      title: '注册时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (text) => dayjs(text).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: '操作',
      key: 'action',
      width: 250,
      fixed: 'right',
      render: (_, record) => (
        <Space wrap>
          <Button
            type="link"
            size="small"
            icon={<CrownOutlined />}
            onClick={() => handleVIP(record)}
          >
            会员
          </Button>
          <Popconfirm
            title={record.is_tester === 1 ? '确定要取消测试权限吗？' : '确定要设置为测试用户吗？'}
            onConfirm={() => handleToggleTester(record)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              size="small"
              icon={<ExperimentOutlined />}
              style={{ color: record.is_tester === 1 ? '#722ed1' : undefined }}
            >
              {record.is_tester === 1 ? '取消测试' : '设为测试'}
            </Button>
          </Popconfirm>
          {record.status === 1 ? (
            <Button
              type="link"
              size="small"
              danger
              icon={<StopOutlined />}
              onClick={() => handleBan(record)}
            >
              封禁
            </Button>
          ) : (
            <Button
              type="link"
              size="small"
              icon={<CheckCircleOutlined />}
              onClick={() => handleUnban(record.id)}
            >
              解封
            </Button>
          )}
          <Popconfirm
            title="确定要删除该用户吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <h2>用户管理</h2>
        <p>管理App用户，查看用户状态，封禁或删除用户</p>
      </div>

      <Card bordered={false}>
        <Space style={{ marginBottom: 16 }} wrap>
          <Input
            placeholder="搜索用户名/昵称/邮箱/手机"
            prefix={<SearchOutlined />}
            style={{ width: 240 }}
            value={filters.keyword}
            onChange={(e) => setFilters({ ...filters, keyword: e.target.value })}
            onPressEnter={handleSearch}
          />
          <Select
            placeholder="用户状态"
            style={{ width: 120 }}
            allowClear
            value={filters.status}
            onChange={(value) => setFilters({ ...filters, status: value })}
          >
            <Option value={1}>正常</Option>
            <Option value={0}>禁用</Option>
          </Select>
          <Select
            placeholder="会员状态"
            style={{ width: 120 }}
            allowClear
            value={filters.is_vip}
            onChange={(value) => setFilters({ ...filters, is_vip: value })}
          >
            <Option value={1}>VIP</Option>
            <Option value={0}>普通</Option>
          </Select>
          <Button type="primary" onClick={handleSearch}>
            搜索
          </Button>
        </Space>

        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          scroll={{ x: 920 }}
          size="small"
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => setPagination({ ...pagination, current: page, pageSize }),
          }}
        />
      </Card>

      {/* 封禁弹窗 */}
      <Modal
        title="封禁用户"
        open={banModalVisible}
        onOk={handleBanSubmit}
        onCancel={() => setBanModalVisible(false)}
        okText="确定"
        cancelText="取消"
      >
        <Form form={banForm} layout="vertical">
          <Form.Item
            name="reason"
            label="封禁原因"
            rules={[{ required: true, message: '请输入封禁原因' }]}
          >
            <Input.TextArea rows={4} placeholder="请输入封禁原因" />
          </Form.Item>
        </Form>
      </Modal>

      {/* VIP弹窗 */}
      <Modal
        title="设置会员"
        open={vipModalVisible}
        onOk={handleVIPSubmit}
        onCancel={() => setVipModalVisible(false)}
        okText="确定"
        cancelText="取消"
      >
        <Form form={vipForm} layout="vertical">
          <Form.Item
            name="vip_level"
            label="会员等级"
            rules={[{ required: true, message: '请选择会员等级' }]}
          >
            <Select>
              <Option value={0}>普通用户</Option>
              <Option value={1}>月度会员</Option>
              <Option value={2}>年度会员</Option>
              <Option value={3}>永久会员</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="days"
            label="有效天数"
            rules={[{ required: true, message: '请输入有效天数' }]}
            extra="永久会员此项无效"
          >
            <Input type="number" min={1} placeholder="请输入有效天数" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserList;

